import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { AIReceptionistChat } from "@/components/AIReceptionistChat";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, PhoneCall, CalendarCheck, ChatCircleText, ShieldCheck, CheckCircle, Star, Lightning, Quotes } from "@phosphor-icons/react";

const C = { bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00", text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.08)" };
const HEAD = { fontFamily: "'Outfit', sans-serif" };

const VERTICALS = {
  general: {
    label: "Service Businesses",
    eyebrow: "iYeneh SI Receptionist",
    headline: "Turn missed calls into booked business.",
    sub: "iYeneh's SI receptionist answers when you can't, captures serious leads, books appointments, and sends you a clear summary — 24/7, using your business name and hours.",
    accent: C.orange,
    pains: [
      "New customers hang up and call a competitor when no one answers",
      "Calls come in while you're busy serving someone else",
      "After-hours and lunch-hour calls go straight to voicemail",
      "You replay long voicemails instead of getting a clean summary",
    ],
    features: [
      { icon: PhoneCall, title: "Answers every call", desc: "Greets callers professionally with your business name, hours and approved info." },
      { icon: CalendarCheck, title: "Books appointments", desc: "Collects the details and books an available time right on the phone." },
      { icon: ChatCircleText, title: "Captures new leads", desc: "Records name, number, need and urgency so you can follow up fast." },
      { icon: ShieldCheck, title: "Sends summaries", desc: "Delivers a clean call summary instead of forcing you to replay calls." },
    ],
    faqs: [
      { q: "Do I need to change my number?", a: "No — in most cases your current number simply forwards to the SI receptionist." },
      { q: "Will it replace my staff?", a: "It handles repetitive front-desk calls and captures info; your team stays in control of decisions and follow-up." },
      { q: "What businesses is it for?", a: "Dental, home services, salons, legal, real estate, repair shops and other appointment- or lead-driven businesses." },
    ],
  },
  dentists: {
    label: "Dental Practices",
    eyebrow: "SI Receptionist for Dentists",
    headline: "Never miss another new-patient call.",
    sub: "Your SI receptionist answers every call 24/7, books and reschedules appointments, and texts confirmations — so your front desk can focus on the patients in the chair.",
    accent: C.green,
    pains: [
      "New patients hang up and call the next practice when no one answers",
      "Front desk is buried during cleanings and can't reach the phone",
      "After-hours and lunch-hour calls go straight to voicemail",
      "No-shows quietly drain thousands in chair time every month",
    ],
    features: [
      { icon: PhoneCall, title: "Answers every call", desc: "Picks up on the first ring, day or night, in a warm natural voice." },
      { icon: CalendarCheck, title: "Books & reschedules", desc: "Checks your calendar and books cleanings, exams and emergencies by phone." },
      { icon: ChatCircleText, title: "Texts confirmations", desc: "Instant SMS + email confirmation, plus a day-before reminder to cut no-shows." },
      { icon: ShieldCheck, title: "Sounds like your office", desc: "Custom greeting, your hours, your services — patients feel taken care of." },
    ],
    faqs: [
      { q: "Will patients know it's SI?", a: "It answers in a natural, friendly voice and handles booking end-to-end. Most callers simply feel like they reached a helpful front desk." },
      { q: "Does it work with my calendar?", a: "Yes — appointments land on your practice calendar, and patients can reschedule or cancel by phone." },
      { q: "What about emergencies?", a: "You set the rules — urgent calls can be routed straight to your on-call line." },
    ],
  },
  hvac: {
    label: "HVAC & Home Services",
    eyebrow: "SI Receptionist for HVAC",
    headline: "Capture every service call — even when you're on a roof.",
    sub: "Your SI receptionist answers 24/7, books service and install appointments, and texts the details to your team — so a missed call never becomes a lost job.",
    accent: C.orange,
    pains: [
      "Calls come in while techs are on-site with their hands full",
      "Emergency no-heat / no-cool calls at night go unanswered",
      "Homeowners call three companies — whoever answers first wins",
      "Double-booked trucks and forgotten follow-ups cost real money",
    ],
    features: [
      { icon: PhoneCall, title: "24/7 call answering", desc: "Every ring answered — nights, weekends and peak-season rushes included." },
      { icon: CalendarCheck, title: "Books service calls", desc: "Schedules diagnostics, tune-ups and installs right into your dispatch calendar." },
      { icon: ChatCircleText, title: "Texts the job details", desc: "Address, issue and time sent by SMS instantly, with reminders before arrival." },
      { icon: ShieldCheck, title: "Prioritizes emergencies", desc: "No-heat and no-cool calls get flagged and routed the way you choose." },
    ],
    faqs: [
      { q: "Can it handle after-hours emergencies?", a: "Yes — you decide which calls are urgent, and it can route them straight to your on-call tech." },
      { q: "Will it fit my scheduling?", a: "Appointments drop onto your calendar and can be rescheduled by phone, so trucks stay full without double-booking." },
      { q: "Do I need new hardware?", a: "No — it works with a business number on iYeneh Connect. Nothing to install." },
    ],
  },
  salons: {
    label: "Salons & Spas",
    eyebrow: "SI Receptionist for Salons & Spas",
    headline: "Keep your chairs full — without stopping mid-appointment.",
    sub: "Your SI receptionist answers every call, books and reschedules clients, and sends confirmations — so your stylists never have to put down their scissors to answer the phone.",
    accent: C.green,
    pains: [
      "Phone rings mid-cut and clients get sent to voicemail",
      "New clients book with whoever picks up first",
      "Last-minute cancellations leave gaps no one fills",
      "Front desk juggling checkout, walk-ins and the phone at once",
    ],
    features: [
      { icon: PhoneCall, title: "Answers hands-free", desc: "Every call picked up so stylists stay focused on the client in the chair." },
      { icon: CalendarCheck, title: "Books by service & stylist", desc: "Schedules cuts, color and treatments to the right stylist and time slot." },
      { icon: ChatCircleText, title: "Confirms & reminds", desc: "Instant confirmation texts and day-before reminders keep chairs full." },
      { icon: ShieldCheck, title: "On-brand & warm", desc: "Your greeting, your services, your vibe — clients feel looked after." },
    ],
    faqs: [
      { q: "Can clients pick their stylist?", a: "Yes — it books to the right stylist and service, and clients can reschedule by phone." },
      { q: "Will it reduce no-shows?", a: "Confirmation texts and day-before reminders are proven to cut no-shows significantly." },
      { q: "How fast can we start?", a: "Create your free business account and turn it on in minutes — no hardware needed." },
    ],
  },
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <div className={`vlx-up ${className}`} style={{ animationDelay: `${delay}ms` }}>{children}</div>
);

function MissedCallCalculator() {
  const [calls, setCalls] = useState(10);
  const [value, setValue] = useState(300);
  const monthly = Math.round(calls * 4.3 * value * 0.3);
  return (
    <section className="border-t py-16" style={{ borderColor: C.border }} data-testid="calculator">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.orange }}>The cost of silence</p>
        <h2 style={HEAD} className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">What could missed calls be costing you?</h2>
      </Reveal>
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        <Reveal delay={60}>
          <div className="flex h-full flex-col justify-between rounded-2xl border p-6" style={{ background: C.card, borderColor: C.border }}>
            <label className="text-sm font-semibold" style={{ color: C.sub }}>Missed calls / week</label>
            <p style={HEAD} className="my-2 text-4xl font-black text-white">{calls}</p>
            <input data-testid="calc-calls" type="range" min="1" max="50" value={calls} onChange={(e) => setCalls(+e.target.value)} className="mt-2 w-full" style={{ accentColor: C.orange }} />
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="flex h-full flex-col justify-between rounded-2xl border p-6" style={{ background: C.card, borderColor: C.border }}>
            <label className="text-sm font-semibold" style={{ color: C.sub }}>Average sale value</label>
            <p style={HEAD} className="my-2 text-4xl font-black text-white">${value}</p>
            <input data-testid="calc-value" type="range" min="50" max="2000" step="50" value={value} onChange={(e) => setValue(+e.target.value)} className="mt-2 w-full" style={{ accentColor: C.orange }} />
          </div>
        </Reveal>
        <Reveal delay={180}>
          <div className="relative flex h-full flex-col items-center justify-center overflow-hidden rounded-2xl border p-6 text-center" style={{ borderColor: "rgba(16,185,129,0.3)", background: "linear-gradient(160deg,rgba(16,185,129,0.14),#0F1729 60%,#070B16)", boxShadow: "0 0 44px rgba(16,185,129,0.15)" }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.sub }}>Potential opportunity / month</p>
            <p style={{ ...HEAD, color: C.green }} className="my-1 text-4xl font-black sm:text-5xl" data-testid="calc-result">${monthly.toLocaleString()}</p>
            <p className="text-[11px]" style={{ color: C.sub }}>Recoverable with an SI receptionist. Estimate, not a guarantee.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function VerticalLanding() {
  const { vertical } = useParams();
  const v = VERTICALS[(vertical || "general").toLowerCase()] || VERTICALS.general;
  const registerUrl = `/register?next=${encodeURIComponent("/app/appointments")}&type=business`;

  useEffect(() => { api.post("/track/tap", { slug: "appointments" }).catch(() => {}); }, []);

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope',sans-serif" }} className="relative min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes vlxUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
        .vlx-up{animation:vlxUp .7s cubic-bezier(.16,1,.3,1) both}
        @keyframes vlxPulse{0%,100%{opacity:.5}50%{opacity:1}}
      `}</style>

      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full" style={{ background: "rgba(255,122,0,0.14)", filter: "blur(150px)" }} />
        <div className="absolute -bottom-40 -left-32 h-[28rem] w-[28rem] rounded-full" style={{ background: "rgba(16,185,129,0.12)", filter: "blur(150px)" }} />
      </div>

      <div className="relative z-10">
        {/* HEADER */}
        <header className="sticky top-0 z-40 border-b backdrop-blur-2xl" style={{ borderColor: C.border, background: "rgba(7,11,22,0.8)" }}>
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <Link to="/services" data-testid="vertical-back" className="flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-white"><ArrowLeft size={18} /> All services</Link>
            <div className="hidden items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider sm:flex" style={{ borderColor: "rgba(16,185,129,0.25)", background: "rgba(16,185,129,0.08)", color: C.green }}>
              <span className="h-2 w-2 rounded-full" style={{ background: C.green, animation: "vlxPulse 1.6s ease-in-out infinite" }} /> SI Receptionist active
            </div>
            <Link to={registerUrl} data-testid="vertical-cta-top" className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: "linear-gradient(90deg,#FF7A00,#FF5500)", boxShadow: "0 8px 22px rgba(255,122,0,0.32)" }}>Start free</Link>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 sm:px-6" data-testid={`vertical-page-${(vertical || "general").toLowerCase()}`}>
          {/* HERO + LIVE CHAT */}
          <section className="grid grid-cols-1 items-center gap-10 py-12 sm:py-16 lg:grid-cols-12 lg:gap-12 lg:py-20" data-testid="live-demo">
            <div className="lg:col-span-6">
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider" style={{ borderColor: "rgba(255,122,0,0.3)", background: "rgba(255,122,0,0.1)", color: C.orange }}>
                  <Lightning size={14} weight="fill" /> {v.eyebrow}
                </span>
              </Reveal>
              <Reveal delay={80}>
                <h1 style={HEAD} className="mt-5 text-4xl font-black leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">{v.headline}</h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{v.sub}</p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link to={registerUrl} data-testid="vertical-cta-hero" className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-extrabold text-white transition-all duration-300 hover:-translate-y-1" style={{ background: "linear-gradient(90deg,#FF7A00,#FF5500)", boxShadow: "0 12px 30px rgba(255,122,0,0.35)" }}>
                    Get my SI receptionist <ArrowRight size={18} weight="bold" />
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={300}>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-slate-400">
                  <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> From $19.99/mo</span>
                  <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> Instant setup</span>
                  <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> No hardware needed</span>
                </div>
              </Reveal>
            </div>

            <Reveal delay={200} className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-[2rem] border p-4 backdrop-blur-2xl sm:p-5" style={{ borderColor: "rgba(255,255,255,0.12)", background: "linear-gradient(to bottom,rgba(15,23,41,0.9),rgba(7,11,22,0.9))", boxShadow: "0 25px 60px rgba(0,0,0,0.6)" }}>
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full" style={{ background: "rgba(255,122,0,0.18)", filter: "blur(70px)" }} />
                <div className="relative mb-3 flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-slate-300">Live conversation</span>
                  <span className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold" style={{ borderColor: "rgba(16,185,129,0.25)", background: "rgba(16,185,129,0.1)", color: C.green }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: C.green, animation: "vlxPulse 1.4s ease-in-out infinite" }} /> Online
                  </span>
                </div>
                <AIReceptionistChat height={380} quickReplies={["I need an SI receptionist", "Book a consultation", "What can you do?"]} />
                <p className="relative mt-3 text-center text-xs text-slate-400">This is the real receptionist — talk to it now. It uses your business name, hours &amp; services.</p>
              </div>
            </Reveal>
          </section>

          {/* PAINS */}
          <section className="border-t py-16" style={{ borderColor: C.border }}>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.orange }}>The problem</p>
              <h2 style={HEAD} className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">Sound familiar?</h2>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {v.pains.map((p, i) => (
                <Reveal key={i} delay={i * 70}>
                  <div className="group flex h-full items-start gap-4 rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-0.5" style={{ borderColor: "rgba(239,68,68,0.15)", background: "linear-gradient(135deg,#111a2e,#0F1729)" }} data-testid={`pain-${i}`}>
                    <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-black" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>✕</span>
                    <p className="text-sm leading-relaxed text-slate-200">{p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* FEATURES */}
          <section className="border-t py-16" style={{ borderColor: C.border }}>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.orange }}>What you get</p>
              <h2 style={HEAD} className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">What it does for {v.label.toLowerCase()}</h2>
            </Reveal>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {v.features.map((f, i) => (
                <Reveal key={i} delay={i * 70}>
                  <div className="group flex h-full flex-col rounded-2xl border p-6 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5" style={{ borderColor: C.border, background: "rgba(17,26,46,0.9)" }} data-testid={`feature-${i}`}>
                    <div className="grid h-12 w-12 place-items-center rounded-xl border transition-transform duration-300 group-hover:scale-110" style={{ background: "linear-gradient(135deg,rgba(255,122,0,0.2),rgba(255,122,0,0.05))", borderColor: "rgba(255,122,0,0.2)", color: C.orange }}>
                      <f.icon size={22} weight="duotone" />
                    </div>
                    <h3 style={HEAD} className="mt-4 text-lg font-bold tracking-tight">{f.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* HOW IT WORKS — TIMELINE */}
          <section className="relative border-t py-16" style={{ borderColor: C.border }}>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.orange }}>Getting started</p>
              <h2 style={HEAD} className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">Live in 3 steps</h2>
            </Reveal>
            <div className="relative mt-10">
              <div className="absolute left-0 right-0 top-9 hidden h-0.5 md:block" style={{ background: "linear-gradient(90deg,transparent,rgba(255,122,0,0.4),transparent)" }} />
              <div className="relative grid gap-6 md:grid-cols-3">
                {[["1", "Create your free account", "Sign up and add your business number in minutes."],
                  ["2", "Set your hours & services", "Tell it your greeting, services and calendar."],
                  ["3", "Turn it on", "It answers every call and fills your calendar 24/7."]].map(([n, t, d], i) => (
                  <Reveal key={n} delay={i * 90}>
                    <div className="rounded-2xl border p-6 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1" style={{ borderColor: C.border, background: "rgba(15,23,41,0.9)" }} data-testid={`step-${n}`}>
                      <div className="grid h-10 w-10 place-items-center rounded-full text-base font-black text-white" style={{ background: "linear-gradient(90deg,#FF7A00,#FF5500)", boxShadow: "0 8px 20px rgba(255,122,0,0.25)" }}>{n}</div>
                      <h3 style={HEAD} className="mt-4 font-bold">{t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{d}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* SOCIAL PROOF */}
          <section className="border-t py-16" style={{ borderColor: C.border }}>
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl border p-8 backdrop-blur-2xl sm:p-12" style={{ borderColor: "rgba(255,255,255,0.12)", background: "linear-gradient(135deg,#0F1729,#111a2e 55%,#070B16)" }}>
                <Quotes size={64} weight="fill" className="absolute -right-2 -top-2 opacity-10" style={{ color: C.orange }} />
                <div className="flex gap-1.5" style={{ color: C.orange }}>
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={18} weight="fill" />)}
                </div>
                <p className="mt-5 max-w-3xl text-xl font-medium italic leading-relaxed text-slate-100 sm:text-2xl">
                  "We stopped losing new bookings to voicemail. The SI answers every call and our calendar fills itself — even after hours."
                </p>
                <div className="mt-6 flex items-center gap-4 border-t pt-6" style={{ borderColor: C.border }}>
                  <img src="https://images.pexels.com/photos/7689734/pexels-photo-7689734.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200" alt="Business owner" className="h-12 w-12 rounded-full object-cover" style={{ border: "2px solid rgba(255,122,0,0.4)" }} />
                  <div>
                    <p className="text-sm font-bold text-white">A busy {v.label.toLowerCase().replace(/s$/, "")} owner</p>
                    <p className="text-xs text-slate-400">Verified iYeneh Connect customer</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>

          <MissedCallCalculator />

          {/* FAQ */}
          <section className="mx-auto max-w-4xl border-t py-16" style={{ borderColor: C.border }}>
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.orange }}>Good to know</p>
              <h2 style={HEAD} className="mt-2 text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">Frequently asked questions</h2>
            </Reveal>
            <div className="mt-8 space-y-3">
              {v.faqs.map((f, i) => (
                <Reveal key={i} delay={i * 70}>
                  <div className="rounded-2xl border p-6 backdrop-blur-xl transition-colors hover:border-white/20" style={{ borderColor: C.border, background: "rgba(17,26,46,0.8)" }} data-testid={`faq-${i}`}>
                    <p className="flex items-center gap-2.5 font-semibold text-white"><CheckCircle size={18} weight="fill" style={{ color: v.accent }} /> {f.q}</p>
                    <p className="mt-2 pl-7 text-sm leading-relaxed text-slate-400">{f.a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="pb-24">
            <Reveal>
              <div className="relative flex flex-col items-center gap-6 overflow-hidden rounded-3xl border p-10 text-center backdrop-blur-2xl sm:p-16" style={{ borderColor: "rgba(255,255,255,0.12)", background: "linear-gradient(90deg,rgba(255,122,0,0.15),#0F1729 50%,rgba(16,185,129,0.15))" }}>
                <div className="absolute -left-20 top-0 h-56 w-56 rounded-full" style={{ background: "rgba(255,122,0,0.18)", filter: "blur(90px)" }} />
                <div className="absolute -right-20 bottom-0 h-56 w-56 rounded-full" style={{ background: "rgba(16,185,129,0.16)", filter: "blur(90px)" }} />
                <h2 style={HEAD} className="relative text-3xl font-black tracking-tight sm:text-4xl">Stop missing calls. Start filling your calendar.</h2>
                <p className="relative max-w-md text-sm text-slate-300 sm:text-base">Set up your SI receptionist today — from $19.99/mo, cancel anytime.</p>
                <Link to={registerUrl} data-testid="vertical-cta-bottom" className="relative mt-1 inline-flex items-center gap-2 rounded-full px-9 py-4 text-base font-extrabold text-white transition-all duration-300 hover:-translate-y-1" style={{ background: "linear-gradient(90deg,#FF7A00,#FF5500)", boxShadow: "0 14px 34px rgba(255,122,0,0.4)" }}>
                  Get started free <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </Reveal>
          </section>
        </div>
      </div>
      <WhatsAppFab />
    </div>
  );
}
